import React, { useEffect, useState } from 'react';
import { IndianRupee, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { settlePatientBalance, type PaymentRow } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'other';

interface SettleBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  clinicId: string;
  outstandingAmount: number;
  onSettled: () => void;
}

export function SettleBalanceModal({
                                      isOpen,
                                      onClose,
                                      patientId,
                                      clinicId,
                                      outstandingAmount,
                                      onSettled,
                                    }: SettleBalanceModalProps) {
  const [amount, setAmount]                 = useState(0);
  const [paymentMode, setPaymentMode]       = useState<PaymentMode>('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [upiApp, setUpiApp]                 = useState('');
  const [bankName, setBankName]             = useState('');
  const [loading, setLoading]               = useState(false);
  const { formatCurrency } = useBillingUtils();

  useEffect(() => {
    if (isOpen) setAmount(outstandingAmount);
  }, [isOpen, outstandingAmount]);

  const handleClose = () => {
    setAmount(0);
    setPaymentMode('cash');
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
    onClose();
  };

  const handleModeChange = (mode: string) => {
    setPaymentMode(mode as PaymentMode);
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
  };

  const handleSubmit = async () => {
    if (amount <= 0 || amount > outstandingAmount) return;
    setLoading(true);
    try {
      const payment: PaymentRow = {
        amount,
        payment_mode:     paymentMode,
        reference_number: referenceNumber || undefined,
        upi_app:          upiApp          || undefined,
        bank_name:        bankName        || undefined,
      };
      const result = await settlePatientBalance(patientId, clinicId, [payment]);
      toast({
        title: 'Balance settled',
        description: `${formatCurrency(result.total_settled)} applied across ${result.allocations.length} invoice${result.allocations.length === 1 ? '' : 's'}`,
      });
      handleClose();
      onSettled();
    } catch (err: any) {
      toast({ title: 'Settlement failed', description: err.message || 'Could not settle balance', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-success" />
              Settle Outstanding Balance
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="medical-section p-4 bg-muted/30 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Outstanding (Past Due):</span>
                <span className="font-semibold text-warning">{formatCurrency(outstandingAmount)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Applied automatically to the oldest unpaid invoices first.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Amount to Settle *</label>
              <div className="relative">
                <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                    type="number"
                    min="0"
                    max={outstandingAmount}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(Math.min(outstandingAmount, Math.max(0, parseFloat(e.target.value) || 0)))}
                    placeholder="Enter amount"
                    className="pl-10"
                />
              </div>
              {amount > outstandingAmount && (
                  <div className="text-xs text-destructive flex items-center gap-1 mt-1.5">
                    <AlertCircle className="w-3 h-3" />
                    Amount exceeds outstanding balance
                  </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Payment Method</label>
              <Select value={paymentMode} onValueChange={handleModeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="netbanking">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Insurance / Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentMode === 'upi' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">UPI App</label>
                    <Select value={upiApp} onValueChange={setUpiApp}>
                      <SelectTrigger><SelectValue placeholder="Select app (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpay">Google Pay</SelectItem>
                        <SelectItem value="phonepe">PhonePe</SelectItem>
                        <SelectItem value="paytm">Paytm</SelectItem>
                        <SelectItem value="bhim">BHIM</SelectItem>
                        <SelectItem value="other">Other UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">UPI Transaction ID</label>
                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                           placeholder="e.g. 123456789012" className="font-mono text-sm" />
                  </div>
                </div>
            )}

            {paymentMode === 'cheque' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Cheque Number</label>
                    <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                           placeholder="Enter cheque number" className="font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Bank Name</label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. SBI, HDFC, ICICI" />
                  </div>
                </div>
            )}

            {paymentMode === 'netbanking' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Transaction Reference</label>
                  <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                         placeholder="NEFT/RTGS reference number" className="font-mono text-sm" />
                </div>
            )}

            {paymentMode === 'card' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Card Last 4 Digits</label>
                  <Input value={referenceNumber}
                         onChange={(e) => setReferenceNumber(e.target.value.replace(/\D/g, '').slice(0, 4))}
                         placeholder="e.g. 4321" maxLength={4} className="font-mono text-sm w-24" />
                </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button
                onClick={handleSubmit}
                disabled={loading || amount <= 0 || amount > outstandingAmount}
                className="bg-success hover:bg-success/80 text-success-foreground"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Settling...</> : 'Settle Balance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
